import { useState } from "react";
import { Search } from "lucide-react";
import { fmtIDR, fmtDate } from "../lib/format";

export function PageHeader({ title, subtitle, children, testid }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-6" data-testid={testid}>
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-100" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{title}</h1>
        {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export function Card({ children, className = "", testid }) {
  return (
    <div className={`bg-slate-900/90 border border-slate-800/80 rounded-xl p-5 ${className}`} data-testid={testid}>
      {children}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, tone = "emerald", testid, sub }) {
  const tones = {
    emerald: "bg-emerald-500/10 text-emerald-400",
    amber: "bg-amber-500/10 text-amber-400",
    rose: "bg-rose-500/10 text-rose-400",
    indigo: "bg-indigo-500/10 text-indigo-400",
    cyan: "bg-cyan-500/10 text-cyan-400",
  };
  return (
    <Card testid={testid} className="hover:border-slate-700/80 transition-colors">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</div>
          <div className="text-xl sm:text-2xl font-semibold mt-1.5 text-slate-100" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        {Icon && <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="w-5 h-5" /></div>}
      </div>
    </Card>
  );
}

export function Badge({ children, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-500/15 text-slate-300 border-slate-500/40",
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    rose: "bg-rose-500/15 text-rose-300 border-rose-500/40",
    indigo: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40",
    cyan: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
    orange: "bg-orange-500/15 text-orange-300 border-orange-500/40",
  };
  return <span className={`inline-flex items-center text-[11px] font-medium border rounded-full px-2.5 py-0.5 ${tones[tone] || tones.slate}`}>{children}</span>;
}

export const statusTone = (s) => ({
  tersedia: "emerald", booking: "amber", terjual: "rose", hold: "indigo",
  draft: "slate", menunggu_approval: "indigo", approved: "emerald", rejected: "rose",
  pending: "amber", unpaid: "rose", paid: "emerald", partial: "amber",
  received: "emerald", partial_received: "amber", converted: "cyan",
  lunas: "emerald", belum: "slate", sebagian: "amber", batal: "rose", berjalan: "cyan", selesai: "emerald",
  dibangun: "indigo", menunggu_ttd: "amber", signed: "emerald", final: "emerald",
}[s] || "slate");

export function Modal({ open, onClose, title, children, wide, testid }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className={`relative bg-slate-900 border border-slate-700 rounded-xl w-full ${wide ? "max-w-4xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto p-4 sm:p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-100" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{title}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-white text-xl leading-none" data-testid={`${testid}-close`}>&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-xs font-mono uppercase tracking-wider text-slate-400 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export const inputCls = "w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500/60 transition-colors";

export function Btn({ children, tone = "primary", className = "", ...props }) {
  const tones = {
    primary: "bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-600",
    ghost: "bg-transparent hover:bg-slate-800 text-slate-300 hover:text-white border-slate-700",
    danger: "bg-rose-600/10 hover:bg-rose-600/20 text-rose-300 border-rose-500/40",
    amber: "bg-amber-600/10 hover:bg-amber-600/20 text-amber-300 border-amber-500/40",
  };
  return (
    <button
      className={`inline-flex items-center gap-1.5 text-sm font-medium border rounded-lg px-3.5 py-2 transition-colors disabled:opacity-40 ${tones[tone]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function DataTable({ columns, rows, searchKeys = [], testid, empty = "Belum ada data" }) {
  const [q, setQ] = useState("");
  const filtered = q
    ? rows.filter((r) => searchKeys.some((k) => String(typeof k === "function" ? k(r) : r[k] ?? "").toLowerCase().includes(q.toLowerCase())))
    : rows;
  return (
    <div data-testid={testid}>
      {searchKeys.length > 0 && (
        <div className="relative mb-3 max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input className={`${inputCls} pl-9`} placeholder="Cari..." value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`} />
        </div>
      )}
      <div className="overflow-x-auto rounded-xl border border-slate-800">
        <table className="w-full text-xs sm:text-sm">
          <thead>
            <tr className="bg-slate-900 border-b border-slate-800">
              {columns.map((c, i) => (
                <th key={i} className="text-left text-[10px] sm:text-xs font-mono uppercase tracking-wider text-slate-500 px-3 py-2 sm:px-4 sm:py-3 whitespace-nowrap">{c.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={columns.length} className="text-center text-slate-500 py-8">{empty}</td></tr>
            )}
            {filtered.map((r, ri) => (
              <tr key={r.id || ri} className="border-b border-slate-800/60 hover:bg-slate-900/70 transition-colors" data-testid={`${testid}-row-${ri}`}>
                {columns.map((c, ci) => (
                  <td key={ci} className={`px-3 py-2 sm:px-4 sm:py-3 text-slate-300 whitespace-nowrap ${c.mono ? "font-mono text-xs" : ""}`}>
                    {c.render ? c.render(r) : r[c.accessor]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export { fmtIDR, fmtDate };
